/**
 * OfflineConflictBadge — shown on records that were updated from an offline
 * device and may conflict with edits made by another user.
 *
 * Usage:
 *   <OfflineConflictBadge
 *     record={horse}                          // must have offline_update_flag + id
 *     entityType="horses"                     // url segment: horses | crm-entities | sale-opportunities
 *     onDismissed={() => refetchRecord()}     // called after flag is cleared
 *   />
 */
import React, { useState } from 'react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Smartphone, X } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';

export default function OfflineConflictBadge({ record, entityType, onDismissed }) {
  const [dismissing, setDismissing] = useState(false);

  if (!record?.offline_update_flag) return null;

  const handleDismiss = async (e) => {
    e.stopPropagation();
    setDismissing(true);
    try {
      await api.patch(`/${entityType}/${record.id}/dismiss-conflict`);
      if (onDismissed) onDismissed();
    } catch {
      toast.error('Failed to dismiss conflict flag');
    } finally {
      setDismissing(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1 ml-1">
      <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-[10px] px-1.5 py-0.5 flex items-center gap-1 font-medium">
        <Smartphone className="w-3 h-3 shrink-0" />
        Recently Updated from Mobile
      </Badge>
      <Button
        variant="ghost"
        size="sm"
        className="h-5 w-5 p-0 text-amber-600 hover:text-amber-800 hover:bg-amber-100"
        disabled={dismissing}
        onClick={handleDismiss}
        title="Dismiss this flag"
      >
        <X className="w-3 h-3" />
      </Button>
    </span>
  );
}
